import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { donedealBreakpoints } from 'styled-system';
import { QueryParameters } from './queries/schema/carFinance.schema';
import { setStartTime } from '../../helpers/browserStorageTimer';
import { pushToDataLayer } from '../../services/gtmService';
import { ApplicationComplete } from './components/ApplicationComplete/ApplicationComplete';
import { LoadError } from './components/LoadError/LoadError';
import { Introduction } from './components/Panels/Introduction/Introduction';
import { SwitchOnProvider } from './components/SwitchOnProvider/SwitchOnProvider';
import { FINANCE_APPLICATION_START_TIME } from './constants';
import {
  onConsentSubmitEvent,
  onStepProgressionEvent,
  onSubmitTrackingEvent,
} from './financeProviders/shared/tracking/tracking';
import { RESULT } from './financeProviders/shared/tracking/tracking.typed';
import { useGetCarFinanceInitialFormData } from './queries/hooks/useGetCarFinanceInitialFormData';
import { Loading } from './components/Loading/Loading';
import { Spacer } from '../../helpers/genericStyles';

const Wrapper = styled.div`
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  min-height: 720px;

  ${donedealBreakpoints.media.medium} {
    padding: ${({ theme }) => `${theme.spacing.M24} 0 ${theme.spacing.L56}`};
  }
`;

const Panel = styled.div`
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => `${theme.spacing.M24} ${theme.spacing.M16}`};

  ${donedealBreakpoints.media.medium} {
    padding: ${({ theme }) => theme.spacing.M24};
  }
`;

const PROVIDER_TRACKING_NAME: Record<QueryParameters['provider'], string> = {
  closebrothers: 'Closebrothers',
  bankofireland: 'Bankofireland',
};

const SELLER_TYPE_TRACKING_NAME: Record<string, string> = {
  DEALER: 'Pro',
  TRADER: 'Trader',
  PRIVATE: 'Private',
  NONE: 'None',
};

type CarFinancePageProps = { query: QueryParameters };

const CarFinancePage = ({ query }: CarFinancePageProps) => {
  const {
    adId,
    dealerId,
    location,
    mainSection,
    make,
    model,
    platform,
    price,
    pricingIndicator,
    provider,
    section,
    sellerType,
    trustedDealer,
    userId,
    year,
  } = query;

  const {
    isPending: initialFormDataPending,
    data: initialFormData,
    isError: initialFormDataError,
  } = useGetCarFinanceInitialFormData(query);

  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const hasTrackedEnquiry = useRef(false);
  const hasTrackedLoad = useRef(false);
  const prevStepRef = useRef(1);

  const handleStepChange = useCallback((step: number) => {
    const prevStep = prevStepRef.current;
    if (step > prevStep) onStepProgressionEvent(prevStep);
    if (step > 1 && !hasTrackedEnquiry.current) {
      pushToDataLayer({ event: 'finance_enquiry_complete' });
      hasTrackedEnquiry.current = true;
    }
    prevStepRef.current = step;
    setCurrentStep(step);
  }, []);

  const updateToSuccessState = () => {
    // Two taxonomies: consent_submit_application_button feeds the button-click
    // dashboard; finance_application_submitted feeds the conversion funnel with
    // time-to-submit and provider attribution.
    onConsentSubmitEvent();
    onSubmitTrackingEvent({ result: RESULT.SUCCESS, provider });
    setIsSubmissionSuccessful(true);
  };

  const onSubmitErrorCallback = (errorFields: Array<string>) => {
    onSubmitTrackingEvent({
      result: RESULT.FAILURE,
      provider,
      errorFields,
    });
  };

  useEffect(() => {
    if (
      !localStorage.getItem(FINANCE_APPLICATION_START_TIME) &&
      !sessionStorage.getItem(FINANCE_APPLICATION_START_TIME)
    ) {
      setStartTime(
        FINANCE_APPLICATION_START_TIME,
        new Date().getTime().toString(),
      );
    }
  }, []);

  useEffect(() => {
    if (hasTrackedLoad.current) return;
    hasTrackedLoad.current = true;
    pushToDataLayer({
      event: 'car_finance_form_loaded',
      financeProvider: [PROVIDER_TRACKING_NAME[provider]],
      adId: [adId],
      dealerId: dealerId ? [dealerId] : undefined,
      section: section ? [section] : undefined,
      price: [String(price)],
      sellerType: sellerType
        ? [SELLER_TYPE_TRACKING_NAME[sellerType] ?? sellerType]
        : undefined,
      year: [year],
      make: [make],
      model: [model],
      userId: userId ? [userId] : undefined,
      newPlatform: platform
        ? [Array.isArray(platform) ? platform[0] : platform]
        : undefined,
      vertical: ['Motor'],
      location: location ? [location] : undefined,
      mainSection: mainSection ? [mainSection] : undefined,
      pageType: ['car_finance'],
      pricing_indicator: pricingIndicator ? [pricingIndicator] : undefined,
      trusted_dealer: trustedDealer === 'true',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper>
      <Panel as="div">
        {!isSubmissionSuccessful && (
          <>
            {!initialFormDataPending && initialFormData && (
              <>
                <Introduction
                  provider={provider}
                  // only show logo and heading on first step
                  isStepOne={currentStep === 1}
                />
                <SwitchOnProvider
                  query={query}
                  onSubmitCallback={updateToSuccessState}
                  onSubmitErrorCallback={onSubmitErrorCallback}
                  onStepChange={handleStepChange}
                  initialFormData={initialFormData}
                  adId={adId}
                  make={make}
                  model={model}
                  provider={provider}
                  price={price}
                />
              </>
            )}
            {!initialFormDataPending && initialFormDataError && <LoadError />}
            {initialFormDataPending && <Loading />}
            <Spacer marginBottom={['L40', 'S0']} />
          </>
        )}

        {isSubmissionSuccessful && <ApplicationComplete />}
      </Panel>
    </Wrapper>
  );
};

export { CarFinancePage };
